import { inject, Injectable, InjectionToken, ElementRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Overlay, OverlayConfig as CdkOverlayConfig } from '@angular/cdk/overlay';
import { Breakpoint, MediaQueryService } from '../../services/media-query.service';

import type { TemplateRef, ViewContainerRef } from '@angular/core';
import type {
  ConnectedPosition,
  OverlayRef,
  PositionStrategy
} from '@angular/cdk/overlay';
import type { BreakpointState } from '../../services/media-query.service';

export interface OverlayConfig {
  elementRef: ElementRef;
  overlayTemplate: TemplateRef<unknown>;
  viewContainerRef: ViewContainerRef;
  positions: Array<ConnectedPosition>;
  overlayData: unknown;
  overlaySize: number;
}

export interface OverlayAsyncOpenEvent {
  open(): void;
}

export interface OverlayFactory {
  create(overlayConfig: OverlayConfig): OverlayRef;
}

export const OVERLAY_FACTORY = new InjectionToken<OverlayFactory>('overlayFactory');

@Injectable({
  providedIn: 'root'
})
export class OverlayFactoryService implements OverlayFactory {
  private readonly overlay = inject(Overlay);
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly document = inject(DOCUMENT);

  create(overlayConfig: OverlayConfig): OverlayRef {
    const strategy: PositionStrategy = this.getDefaultPositionStrategy(overlayConfig.elementRef, overlayConfig.positions);
    const overlayWidth = overlayConfig.overlaySize === 0 ? undefined : overlayConfig.overlaySize;
    const config = this.getOverlayConfig(strategy, overlayWidth);
    const overlayRef = this.overlay.create(config);
    return this.observe(overlayRef, strategy, overlayConfig.elementRef);
  }

  private observe(overlayRef: OverlayRef, defaultPositionStrategy: PositionStrategy, elementRef: ElementRef): OverlayRef {
    const subscription = this.mediaQueryService.observe(Breakpoint.SM).subscribe((breakpointState: BreakpointState): void => {
      if (breakpointState.matches) {
        overlayRef.updatePositionStrategy(this.getMobilePositionStrategy());
      } else {
        overlayRef.updatePositionStrategy(defaultPositionStrategy);
      }
    });

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((): void => {
        overlayRef.updatePosition();
      });

      let current: HTMLElement | null = elementRef.nativeElement;
      while (current) {
        resizeObserver.observe(current);
        current = current.parentElement;
      }
    }

    overlayRef.detachments().subscribe((): void => {
      subscription.unsubscribe();
      resizeObserver?.disconnect();
    });
    return overlayRef;
  }

  private getOverlayConfig(strategy: PositionStrategy, overlayWidth: number | undefined): CdkOverlayConfig {
    const isPageScroll = this.document.body.classList.contains('td-wb-modal-page-scroll');
    return new CdkOverlayConfig({
      panelClass: ['td-wb-overlay-panel'],
      positionStrategy: strategy,
      hasBackdrop: true,
      backdropClass: ['td-wb-modal-backdrop', 'td-wb-modal-backdrop--overlay'],
      width: overlayWidth,
      scrollStrategy: isPageScroll ? this.overlay.scrollStrategies.noop() : this.overlay.scrollStrategies.reposition()
    });
  }

  private getDefaultPositionStrategy(elementRef: ElementRef, positions: Array<ConnectedPosition>): PositionStrategy {
    const isPageScroll = this.document.body.classList.contains('td-wb-modal-page-scroll');
    return this.overlay
      .position()
      .flexibleConnectedTo(elementRef)
      .withPush(!isPageScroll)
      .withFlexibleDimensions(false)
      .withGrowAfterOpen(true)
      .withLockedPosition(false)
      .withPositions(positions);
  }

  private getMobilePositionStrategy(): PositionStrategy {
    return this.overlay.position().global().centerVertically().centerHorizontally();
  }

}
